import { Link } from 'react-router-dom';
import { focusWithoutScroll } from './focusUtils';

export interface PauseOverlayProps {
  onResume: () => void;
  onRestart: () => void;
  /**
   * Mode-specific hotkey list shown in the overlay footer. Defaults to
   * the Pulse-mode keys; Circles overrides with click-driven controls.
   */
  hotkeys?: ReadonlyArray<{ key: string; action: string }>;
  /**
   * Where the "Quit" link should send the player. Defaults to the
   * mode picker — bailing out mid-run usually means "I want to do
   * something else", and the picker covers both "play a different
   * mode" and "go home" in one click.
   */
  quitHref?: string;
}

const DEFAULT_HOTKEYS: ReadonlyArray<{ key: string; action: string }> = [
  { key: 'Space', action: 'flip / start' },
  { key: 'Esc / P', action: 'pause / resume' },
  { key: 'R', action: 'restart while paused / retry' },
];

/**
 * The "Paused" modal shown mid-run. Reused across modes; the hotkeys
 * list is the only mode-specific surface and is overridable via the
 * `hotkeys` prop.
 */
export function PauseOverlay({
  onResume,
  onRestart,
  hotkeys = DEFAULT_HOTKEYS,
  quitHref = '/',
}: PauseOverlayProps) {
  return (
    <div className="play-overlay" data-testid="pause-overlay">
      <div className="pause-card">
        <h3>Paused</h3>
        <p className="muted">
          <kbd>Esc</kbd> or <kbd>P</kbd> to resume
        </p>
        <button
          type="button"
          className="btn btn-primary btn-large"
          ref={focusWithoutScroll}
          onClick={onResume}
          data-testid="resume-button"
        >
          Resume
        </button>
        <button type="button" className="btn btn-ghost" onClick={onRestart}>
          Restart <span className="kbd-hint">(R)</span>
        </button>
        <Link to={quitHref} className="btn btn-ghost">
          Quit
        </Link>
        <div className="hotkey-list">
          <strong>Hotkeys</strong>
          <ul>
            {hotkeys.map((h) => (
              <li key={h.key}>
                <kbd>{h.key}</kbd> {h.action}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
